/**
 * WeightStepper — inline triplet [−] [tap-to-edit number] [+] used in active
 * set rows. The center is a button: tapping it opens a richer
 * <WeightEditorSheet> that supports drag-to-adjust, preset chips, and the
 * plate calculator. The ± buttons nudge by the user's smallest-loadable step
 * (1.25kg / 2.5lb).
 *
 * Touch targets are h-10 (40px) — meets the audit standard.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Minus, Plus, Barbell, XCircle } from '@phosphor-icons/react';
import {
    calculatePlates,
    inventoryForUnits,
} from '@/lib/plateMath';

export const WEIGHT = {
    stepper: 'weight-stepper',
    display: 'weight-stepper-display',
    minus: 'weight-stepper-minus',
    plus: 'weight-stepper-plus',
    editor: 'weight-editor',
    editorInput: 'weight-editor-input',
    editorMinus: 'weight-editor-minus',
    editorPlus: 'weight-editor-plus',
    editorApply: 'weight-editor-apply',
    editorClose: 'weight-editor-close',
    editorChip: 'weight-editor-chip', // -{value}
    editorPerSide: 'weight-editor-per-side',
};

function stepFor(units) {
    return units === 'lb' ? 2.5 : 1.25;
}

function round2(n) {
    return Math.round(Number(n) * 100) / 100;
}

function formatNum(n) {
    const v = Number(n) || 0;
    return v % 1 === 0 ? String(v) : String(round2(v));
}

export function WeightStepper({ value, onChange, units = 'kg', disabled, setId }) {
    const [editorOpen, setEditorOpen] = useState(false);
    const step = stepFor(units);
    const numeric = Number(value) || 0;

    const nudge = (delta) => {
        const next = Math.max(0, round2(numeric + delta));
        onChange(formatNum(next));
    };

    return (
        <>
            <div
                data-testid={WEIGHT.stepper}
                className="flex items-center gap-1.5"
            >
                <button
                    type="button"
                    onClick={() => nudge(-step)}
                    disabled={disabled || numeric <= 0}
                    data-testid={`${WEIGHT.minus}-${setId}`}
                    aria-label={`Decrease by ${step}`}
                    className="grid place-items-center w-9 h-10 rounded-xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.95] transition-all disabled:opacity-40"
                >
                    <Minus size={14} weight="bold" />
                </button>
                <button
                    type="button"
                    onClick={() => !disabled && setEditorOpen(true)}
                    disabled={disabled}
                    data-testid={`${WEIGHT.display}-${setId}`}
                    aria-label="Edit weight"
                    className="min-w-[64px] h-10 px-2 rounded-xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.97] transition-all font-mono font-extrabold tabular-nums text-lg disabled:opacity-60"
                >
                    {formatNum(value)}
                    <span className="uppercase-mono text-muted-foreground ml-1 text-[10px]">
                        {units}
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => nudge(step)}
                    disabled={disabled}
                    data-testid={`${WEIGHT.plus}-${setId}`}
                    aria-label={`Increase by ${step}`}
                    className="grid place-items-center w-9 h-10 rounded-xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.95] transition-all disabled:opacity-40"
                >
                    <Plus size={14} weight="bold" />
                </button>
            </div>
            <WeightEditorSheet
                open={editorOpen}
                value={numeric}
                units={units}
                onClose={() => setEditorOpen(false)}
                onApply={(v) => {
                    setEditorOpen(false);
                    onChange(formatNum(v));
                }}
            />
        </>
    );
}

/* -------------------- Editor sheet -------------------- */

function WeightEditorSheet({ open, value, units, onClose, onApply }) {
    const [draft, setDraft] = useState(value);
    const dragStartRef = useRef(null);
    const step = stepFor(units);
    const presets = useMemo(
        () =>
            units === 'lb'
                ? [-25, -10, -5, +5, +10, +25, +45]
                : [-10, -5, -2.5, +2.5, +5, +10, +20],
        [units]
    );

    useEffect(() => {
        if (open) setDraft(Number(value) || 0);
    }, [open, value]);

    const inv = inventoryForUnits(units);
    const plates = useMemo(
        () => calculatePlates(draft, inv.barWeight, inv.plates),
        [draft, inv.barWeight, inv.plates]
    );

    if (!open) return null;

    const setDraftClamped = (n) =>
        setDraft(Math.max(0, round2(Number(n) || 0)));

    // Horizontal drag on the big number → ±step per ~14px.
    const onPointerDown = (e) => {
        if (!e.isPrimary) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        dragStartRef.current = { x: e.clientX, start: draft };
    };
    const onPointerMove = (e) => {
        const st = dragStartRef.current;
        if (!st) return;
        const dx = e.clientX - st.x;
        const ticks = Math.round(dx / 14);
        setDraftClamped(st.start + ticks * step);
    };
    const onPointerUp = (e) => {
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
        dragStartRef.current = null;
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Edit weight"
            data-testid={WEIGHT.editor}
            className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-background/80 backdrop-blur-md"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose?.();
            }}
        >
            <div className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-foreground/10 bg-card p-6 sm:p-7 shadow-tactile">
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close editor"
                    data-testid={WEIGHT.editorClose}
                    className="absolute top-4 right-4 grid place-items-center w-10 h-10 rounded-full hover:bg-foreground/10 transition-colors text-muted-foreground"
                >
                    <XCircle size={22} weight="bold" />
                </button>

                <p className="uppercase-mono text-muted-foreground">Set weight</p>
                <h2 className="font-display font-extrabold tracking-tighter text-3xl mt-1">
                    Adjust the load
                </h2>

                {/* Big draggable number */}
                <div
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    className="mt-6 select-none touch-none rounded-3xl border border-foreground/15 bg-foreground/5 px-5 py-5 cursor-ew-resize"
                    style={{ touchAction: 'none' }}
                >
                    <div className="flex items-baseline justify-center gap-2">
                        <input
                            type="number"
                            inputMode="decimal"
                            step={step}
                            min={0}
                            value={draft}
                            onChange={(e) => setDraftClamped(e.target.value)}
                            data-testid={WEIGHT.editorInput}
                            aria-label="Weight"
                            className="bg-transparent outline-none font-mono text-5xl sm:text-6xl font-extrabold tabular-nums text-center w-[6ch] caret-foreground"
                        />
                        <span className="uppercase-mono text-muted-foreground text-base">
                            {units}
                        </span>
                    </div>
                    <p className="mt-1 text-center uppercase-mono text-muted-foreground">
                        ← drag to adjust →
                    </p>
                </div>

                {/* ±step row */}
                <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        onClick={() => setDraftClamped(draft - step)}
                        data-testid={WEIGHT.editorMinus}
                        className="h-12 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all font-bold inline-flex items-center justify-center gap-2"
                    >
                        <Minus size={16} weight="bold" />− {step}
                    </button>
                    <button
                        type="button"
                        onClick={() => setDraftClamped(draft + step)}
                        data-testid={WEIGHT.editorPlus}
                        className="h-12 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all font-bold inline-flex items-center justify-center gap-2"
                    >
                        <Plus size={16} weight="bold" />+ {step}
                    </button>
                </div>

                {/* Preset chips */}
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {presets.map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setDraftClamped(draft + p)}
                            data-testid={`${WEIGHT.editorChip}-${p}`}
                            className={`uppercase-mono inline-flex items-center min-h-9 px-3 rounded-full border transition-all active:scale-[0.97] ${
                                p > 0
                                    ? 'border-[#00FF9D]/30 bg-[#00FF9D]/10 text-[#00FF9D] hover:bg-[#00FF9D]/20'
                                    : 'border-foreground/15 bg-foreground/5 text-muted-foreground hover:text-foreground hover:bg-foreground/10'
                            }`}
                        >
                            {p > 0 ? `+${p}` : p}
                        </button>
                    ))}
                </div>

                {/* Plate math summary */}
                <div className="mt-5 rounded-2xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 flex items-start gap-3">
                    <span className="grid place-items-center w-8 h-8 rounded-lg bg-foreground/5 text-[#FFD63A] shrink-0">
                        <Barbell size={14} weight="bold" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="uppercase-mono text-muted-foreground">
                            Per side ({inv.barWeight}
                            {units} bar)
                        </p>
                        <p
                            data-testid={WEIGHT.editorPerSide}
                            className="font-mono text-sm font-bold mt-0.5 tabular-nums break-words"
                        >
                            {plates.perSide.length === 0
                                ? draft < inv.barWeight
                                    ? `Below bar weight`
                                    : 'Just the bar'
                                : plates.perSide
                                      .map((g) => `${g.count} × ${g.plate}`)
                                      .join(' + ')}
                        </p>
                        {plates.remainder !== 0 && draft >= inv.barWeight && (
                            <p className="text-xs text-[#F4A33A] mt-0.5">
                                Off by{' '}
                                {plates.remainder > 0
                                    ? `+${plates.remainder}`
                                    : plates.remainder}{' '}
                                {units}
                            </p>
                        )}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={() => onApply?.(draft)}
                    data-testid={WEIGHT.editorApply}
                    className="mt-5 w-full h-12 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[1px] active:scale-[0.98] transition-all"
                >
                    Set {formatNum(draft)}
                    {units}
                </button>
            </div>
        </div>
    );
}
